import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { UpdateRiskAssessmentResultMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  namedOperations,
  UpdateRiskAssessmentResultDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

type UpdateRiskAssessmentResultInput = {
  Id: string;
  Rating?: number | null;
  Likelihood?: number | null;
  Impact?: number | null;
  Impacts?: { Label: string; Value: number }[] | null;
  Rationale?: string | null;
  TestDate?: string | null;
  CustomAttributeData?: CustomAttributeData | null;
  AssessmentId?: string | null;
  /**
   * Optional, unlike every sibling entity's token.
   * `risksmart.risk_assessment_result."ModifiedAtTimestamp"` is the one
   * NULLABLE lock column with no default (verified against the database), so a
   * row saved before the column was populated has no token to send. Typing it
   * `string` forced the caller into `?? ''`, and an empty string fails the
   * `.min(1)` the routers now carry — making those rows uneditable. Absent is
   * the correct wire value: the repository skips the lock and the update
   * behaves as an unlocked `Id` + `OrgKey` write, exactly as it did before.
   * The V2 leg is unaffected — `updateRiskAssessmentResult.graphql` never
   * declared the variable.
   */
  OriginalTimestamp?: string | null;
  ParentRiskId?: string | null;
};

export const useUpdateRiskAssessmentResult = createMutationHook({
  name: 'updateRiskAssessmentResult',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.riskAssessmentResult.update.mutationOptions({ onSuccess }),
  mapInput: (input: UpdateRiskAssessmentResultInput) => input,
  invalidate: (trpc) => [
    trpc.frontend.assessment.register.queryKey(),
    trpc.frontend.assessment.resultsRegister.queryKey(),
    trpc.frontend.assessment.riskAssessmentResultsByRiskId.queryKey(),
    trpc.frontend.risk.register.queryKey(),
    trpc.frontend.risk.scores.queryKey(),
    trpc.frontend.risk.riskScoresByRiskId.queryKey(),
    // Fast-path hint only: the risk_score upsert runs in an async event
    // consumer, so this races it — the 5s poll in
    // useGetLatestRiskScoresByRiskId is the convergence backstop.
    trpc.frontend.risk.latestRiskScoresByRiskId.queryKey(),
  ],
  mapResponse: (): UpdateRiskAssessmentResultMutation => ({
    updateChildRiskAssessmentResult: {
      __typename: 'GenericMutationOutput',
      affected_rows: 1,
    },
  }),
  graphqlDocument: UpdateRiskAssessmentResultDocument,
  graphqlVariables: (input: UpdateRiskAssessmentResultInput) => ({ ...input }),
  evictFields: [
    'risk_assessment_result',
    'assessment',
    'risk_assessment_result_aggregate',
  ],
  refetchQueries: [namedOperations.Query.getRiskScoresByRiskId],
});
